"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export default function EditProductPage () {
    const [categories, setCategories] = useState([]);
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState([]);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        product_name: "",
        description: "",
        short_description: "",
        price: "",
        inventory: "",
        category_id: "",
        images: [] // This will contain both existing URLs and new File objects
    });
    useEffect(() => {
        if (id) {
            fetchProductData(id);
        } else {
            setLoading(false);
            toast.error("Product ID is missing");
            navigate("/dashboard/products");
        }
    }, [id, navigate]);

    // 1. Fetch product data and initialize images & previews
    const fetchProductData = async (productId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch("https://leapatinternational.com/admin/api/v1/product.php");

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === "success" && result.data) {
                const product = result.data.find(p => p.id === productId);

                if (product) {
                    let existingImages = [];
                    setFormData(prev => ({
                        ...prev,
                        product_name: product.product_name || "",
                        description: product.description || "",
                        short_description: product.short_description || "",
                        price: product.price || "",
                        inventory: product.inventory || "",
                        category_id: product.category_id || "",
                        images: product.image_gallery || "", // existing images as URLs
                    }));

                    // Set previews for existing images
                    const existingPreviews = product?.image_gallery?.map((url, index) => ({
                        id: `existing-${index}`,
                        url,
                        name: `Existing Image ${index + 1}`,
                        isExisting: true,
                        arrayIndex: index,
                    }));

                    setImagePreview(existingPreviews);
                } else {
                    throw new Error(`Product with ID ${productId} not found`);
                }
            } else {
                throw new Error(result.message || "Failed to fetch products");
            }
        } catch (err) {
            console.error("Error fetching product:", err);
            setError(err.message || "Failed to load product data");
            toast.error(err.message || "Failed to load product data. Please try again.");
        } finally {
            setLoading(false);
        }
    };



    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;


        const newImages = [];
        const newPreviews = [];
        let validFilesCount = 0;

        files.forEach((file, index) => {
            // Check duplicates: match existing image URLs by name or new images by file name and size
            const isDuplicate = formData.images.some(img => {
                if (typeof img === "string") {
                    return false;
                } else {
                    // For new images stored as objects
                    return img.name === file.name && img.file.size === file.size;
                }
            });

            if (isDuplicate) {
                toast.warning(`File "${file.name}" is already selected.`);
                return;
            }

            if (!file.type.startsWith('image/')) {
                toast.error(`File "${file.name}" is not an image.`);
                return;
            }

            const imageId = Date.now() + index + Math.random();
            newImages.push({
                id: imageId,
                file: file,
                name: file.name
            });

            newPreviews.push({
                id: imageId,
                url: URL.createObjectURL(file),
                name: file.name,
            });

            validFilesCount++;
        });

        if (newImages.length > 0) {
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...newImages]
            }));

            setImagePreview(prev => [...prev, ...newPreviews]);

            toast.success(`${validFilesCount} image(s) added successfully!`);
        }

        e.target.value = '';
    };


    // 3. Handle image removal (existing or new)
    const handleRemoveImage = (imageId) => {
        const imageToRemove = imagePreview.find(img => img.id === imageId);
        if (!imageToRemove) return;

        setFormData(prev => {
            if (imageToRemove.isExisting) {
                return {
                    ...prev,
                    images: prev.images.filter(img => img !== imageToRemove.url)
                };
            } else {
                return {
                    ...prev,
                    images: prev.images.filter(img => (typeof img === "string" ? true : img.id !== imageId))
                };
            }
        });
        if (!imageToRemove.isExisting) {
            URL.revokeObjectURL(imageToRemove.url);
        }
        setImagePreview(prev => prev.filter(img => img.id !== imageId));
    };



    const handleRemoveAllImages = () => {
        // Revoke all blob URLs for new images
        imagePreview.forEach(preview => {
            if (!preview.isExisting) {
                URL.revokeObjectURL(preview.url);
            }
        });

        // Clear all images
        setFormData(prev => ({
            ...prev,
            images: []
        }));

        // Clear all previews
        setImagePreview([]);

        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const data = new FormData();
            data.append("id", id);
            data.append("product_name", formData.product_name);
            data.append("description", formData.description);
            data.append("short_description", formData.short_description);
            data.append("price", formData.price);
            data.append("inventory", formData.inventory);
            data.append("category_id", formData.category_id);

            // Append images:
            formData.images.forEach((image, index) => {
                if (typeof image === 'string') {
                    // Existing image URL string
                    data.append(`images[${index}]`, image);
                } else {
                    // New image object with file
                    data.append(`images[${index}]`, image.file);
                }
            });

            const endpoint = "https://leapatinternational.com/admin/api/v1/product_edit.php";

            const response = await fetch(endpoint, {
                method: "POST",
                body: data,
            });

            const result = await response.json();

            if (result.ok === true || result.status === "success") {
                toast.success("Product updated successfully!");
                navigate('/dashboard/products');
            } else {
                toast.error(result.message || "Failed to update product");
            }
        } catch (err) {
            console.error("Error:", err);
            toast.error("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };


    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch("https://leapatinternational.com/admin/api/v1/category.php");

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                setCategories(result.data || []);
            } catch (error) {
                console.error("Error fetching categories:", error);
                toast.error("Failed to load categories");
                setCategories([]);
            }
        };

        fetchCategories();
    }, []);

    // Cleanup object URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            imagePreview.forEach(preview => {
                if (preview.url && preview.url.startsWith('blob:') && !preview.isExisting) {
                    URL.revokeObjectURL(preview.url);
                }
            });
        };
    }, [imagePreview]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCancel = () => {
        navigate('/dashboard/products');
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <h4 className="alert-heading">Error Loading Product</h4>
                <p>{error}</p>
                <button className="back-button" onClick={handleCancel}>
                    ← Back to Products
                </button>
            </div>
        );
    }

    return (
        <div className="category-management-section">
            {/* Page Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-3">

                        <div>
                            <h1 className="page-title mb-0">Edit Product</h1>
                            <p className="page-subtitle mb-0">Update product information</p>
                        </div>
                        <button className="back-button me-3" onClick={handleCancel}>
                            ← Back to Products
                        </button>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Product Information</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Left Column */}
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Product Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="product_name"
                                                value={formData.product_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter product name"
                                                required
                                            />
                                            <div className="form-text">Choose a clear, descriptive name for your product</div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label">Category *</label>
                                            <select
                                                className="form-select"
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select a category</option>
                                                {categories.map((category) => (
                                                    <option key={category.id} value={category.id}>
                                                        {category.title}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="form-text">Choose the most appropriate category for this product</div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="form-label">Price *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="price"
                                                        value={formData.price}
                                                        onChange={handleInputChange}
                                                        placeholder="0.00"
                                                        min="0"
                                                        step="0.01"
                                                        required
                                                    />
                                                    <div className="form-text">Enter product price</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="form-label">Inventory *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="inventory"
                                                        value={formData.inventory}
                                                        onChange={handleInputChange}
                                                        placeholder="0"
                                                        min="0"
                                                        required
                                                    />
                                                    <div className="form-text">Available stock quantity</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="form-label mb-0">Product Images Gallery</label>
                                                {imagePreview.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={handleRemoveAllImages}
                                                    >
                                                        Remove All
                                                    </button>
                                                )}
                                            </div>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                multiple
                                            />
                                            <div className="form-text">
                                                Upload multiple product images (JPG, PNG, GIF). Maximum 10 images, 5MB each.
                                                <br />
                                                <small className="text-muted">
                                                    {formData.images.length}/10 images selected
                                                </small>
                                                <br />
                                                <small className="text-info">
                                                    💡 You can select images multiple times to add more to your gallery
                                                </small>
                                            </div>

                                            {/* Image Gallery Preview */}
                                            {imagePreview.length > 0 && (
                                                <div className="mt-3">
                                                    <label className="form-label">Image Gallery Preview</label>
                                                    <div className="row g-2">
                                                        {imagePreview.map((preview) => (
                                                            <div key={preview.id} className="col-6 col-md-4">
                                                                <div className="position-relative">
                                                                    <img
                                                                        src={preview.url}
                                                                        alt={preview.name}
                                                                        className="img-thumbnail w-100"
                                                                        style={{
                                                                            height: "120px",
                                                                            objectFit: "cover",
                                                                            cursor: "pointer"
                                                                        }}
                                                                        onClick={() => {
                                                                            window.open(preview.url, '_blank');
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                                                        onClick={() => handleRemoveImage(preview.id)}
                                                                        style={{
                                                                            width: "24px",
                                                                            height: "24px",
                                                                            padding: "0",
                                                                            fontSize: "12px",
                                                                            lineHeight: "1"
                                                                        }}
                                                                        title="Remove image"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                    <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white p-1">
                                                                        <small className="text-truncate d-block" style={{ fontSize: "10px" }}>
                                                                            {preview.name}
                                                                            {preview.isExisting && <span className="text-warning"> (Existing)</span>}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Full Width Fields */}
                                <div className="mb-4">
                                    <label className="form-label">Short Description</label>
                                    <textarea
                                        className="form-control"
                                        name="short_description"
                                        value={formData.short_description}
                                        onChange={handleInputChange}
                                        rows="2"
                                        placeholder="Enter a brief product summary"
                                    ></textarea>
                                    <div className="form-text">Brief summary of the product (optional)</div>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label">Product Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="5"
                                        placeholder="Enter detailed product description"
                                    ></textarea>
                                    <div className="form-text">Provide detailed information about the product features and benefits</div>
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <button type="submit" className="add-button" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            "Update Product"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="back-button pe-3 ps-3"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}